import { CONST } from '@/utils/constants'
import { URLS } from '@/utils/urls'
import { setOrDeleteCookie } from '@/utils'
import i18n from '@/i18n'

import Vue from 'vue'
import router from '@/router'
import { mutationTypes } from './types'
import { Users, Workspaces } from '@/api'
import { extractErrors, extractUserId, extractLoginRedirect, extractTheme, extractUserLanguage, extractUserLocale, extractUserAbilities, extractUserStartPage } from '@/data-extraction/users'

export default {
  resetState: ({ commit }) => {
    commit(mutationTypes.RESET_STATE)
  },
  setLanguage: ({ commit }, language) => {
    // Update State
    commit(mutationTypes.SET_LANGUAGE, language)
    // Sync Cookie
    setOrDeleteCookie(CONST.Cookies.i18n.Language, language)
    // Update UI
    i18n.setLanguage()
  },
  setLocale: ({ commit }, locale) => {
    // Update State
    commit(mutationTypes.SET_LOCALE, locale)
    // Sync Cookie
    setOrDeleteCookie(CONST.Cookies.i18n.Locale, locale)
    // Update UI
    i18n.setLanguage()
  },
  login: async ({ commit, dispatch }, { username, password }) => {
    const unexpectedError = [ Vue.i18n.translate('login gateway unreachable') ]
    commit(mutationTypes.LOGIN_PENDING)
    try {
      let response = await Users.login(username, password)
      commit(mutationTypes.LOGIN_SUCCESS)
      return response
    } catch (err) {
      let redirect = extractLoginRedirect(err)
      if(redirect){
        commit(mutationTypes.LOGIN_REDIRECT, redirect)
      } else {
        let errors = extractErrors.login(err) || unexpectedError
        commit(mutationTypes.LOGIN_FAILURE, errors)
        throw errors
      }
    }
  },
  logout: async ({ commit, dispatch }) => {
    const unexpectedError = [ Vue.i18n.translate('login gateway unreachable') ]
    commit(mutationTypes.LOGOUT_PENDING)
    // Ensure Cookie is Removed
    Vue.cookie.delete(CONST.Cookies.SessionExpiration.Name)
    try {
      let response = await Users.logout()
      commit(mutationTypes.LOGOUT_SUCCESS)
      dispatch('resetAllStates', null, { root: true })
      return response
    } catch (err) {
      let errors = extractErrors.logout(err) || unexpectedError
      commit(mutationTypes.LOGOUT_FAILURE, errors)
      dispatch('resetAllStates', null, { root: true })
      throw errors
    }
  },
  setContinuationRoute: async ({ commit }, route) => {
    // Extract info from passed Vue-Router Route-Object
    const hash = route.hash
    const query = route.query
    // Convert Hash to Continue Param (unless Param already exists)
    if (hash) {
      const replacement = (!query.continue) ? { continue: `${CONST.Services.Paths.GWT}${hash}` } : { }
      throw router.replace({ query: { ...query, ...replacement }, hash: '' })
    }
    // Use Query Param
    let address = (query.continue) ? query.continue : null
    // Store Resulting URL
    commit(mutationTypes.SET_CONTINUATION_ROUTE, address)
    // Return Route
    return address
  },
  raiseGeneralError: ({ commit }, message) => {
    commit(mutationTypes.GENERAL_ERROR, message)
  },
  raiseLoginError: ({ commit }, message) => {
    commit(mutationTypes.LOGIN_FAILURE, message)
    return true
  },
  raiseLogoutError: ({ commit }, message) => {
    commit(mutationTypes.LOGOUT_FAILURE, message)
    return true
  },
  loadReports: ({ commit }) => {
    commit(mutationTypes.GET_REPORT_LIST_PENDING)
    return Workspaces.getReportList()
      .then(reports => commit(mutationTypes.GET_REPORT_LIST_SUCCESS, reports))
      .catch(err => commit(mutationTypes.GET_REPORT_LIST_FAILURE, err))
  },
  retrieveCurrentUser: async ({ commit, dispatch }) => {
    let error = undefined
    let isAuthenticated = false
    let currentUser = undefined
    commit(mutationTypes.RETRIEVE_CURRENT_USER_PENDING)
    // Set Basic User Info
    try {
      currentUser = await Users.current()
      isAuthenticated = true
      commit(mutationTypes.RETRIEVE_CURRENT_USER_SUCCESS, {
        userId: extractUserId(currentUser),
        abilities: extractUserAbilities(currentUser),
        startPage: extractUserStartPage(currentUser),
        theme: extractTheme(currentUser)
      })
      const locale = extractUserLocale(currentUser)
      const language = extractUserLanguage(currentUser)
      await dispatch('setLocale', locale)
      await dispatch('setLanguage', language)
    } catch (err) {
      isAuthenticated = false
      error = err
      commit(mutationTypes.RETRIEVE_CURRENT_USER_FAILURE)
    }
    // Return UserInfo or Bubble Error
    if (error || !isAuthenticated) {
      throw error
    } else {
      return currentUser
    }
  }
}
